import React, { useEffect, useState } from 'react';
import { STORAGE_KEY } from '../components/CookieBanner';
import { loadGoogleAnalytics } from '../utils/analytics';
import { isPrerender } from './parts';

// Slim cookie bar pinned to the bottom of the screen for the 2026 pages. Same stored consent
// (key, shape) and the same Accept All / Necessary Only / Manage preferences behavior as the
// legacy CookieBanner, which the old /ai-call-assistant page still uses. The footer's
// "Cookie Settings" link (reopenCookieBanner) clears the stored choice and reloads, so this reappears.
// Fired once the visitor answers, so the phone booking bar (MobileBookBar) can appear without
// ever overlapping this bar.
export const CONSENT_EVENT = 'rb-cookie-consent';

export function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function Toggle({ label, note, checked, onChange, locked = false }) {
  return (
    <label className={`flex items-center justify-between gap-4 py-2.5 ${locked ? '' : 'cursor-pointer'}`}>
      <span>
        <span className="block text-[0.95rem] font-semibold text-ink">{label}</span>
        <span className="block text-[0.875rem] text-ink-mute">{note}</span>
      </span>
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={locked}
        onChange={onChange}
      />
      <span
        aria-hidden="true"
        className={`relative shrink-0 w-10 h-6 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-brand ${
          checked ? 'bg-brand' : 'bg-ink/20'
        } ${locked ? 'opacity-60' : ''}`}
      >
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${checked ? 'left-5' : 'left-1'}`} />
      </span>
    </label>
  );
}

export default function CookieBar() {
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    if (isPrerender()) return undefined;
    const stored = readConsent();
    if (stored) {
      if (stored.analytics) loadGoogleAnalytics();
      return undefined;
    }
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const save = (p) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...p, timestamp: Date.now() }));
    } catch {
      /* storage blocked — still honor the choice for this visit */
    }
    if (p.analytics) loadGoogleAnalytics();
    setVisible(false);
    window.dispatchEvent(new Event(CONSENT_EVENT));
  };
  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const necessaryOnly = () => save({ necessary: true, analytics: false, marketing: false });

  if (!visible) return null;

  return (
    <div style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      className="rb-cookie fixed inset-x-0 bottom-0 z-50 bg-white border-t border-ink/15 shadow-[0_-12px_32px_-20px_rgba(22,26,58,0.45)]" role="region" aria-label="Cookie consent">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3">
        {manage && (
          <div className="pb-2 mb-2 border-b border-ink/10 grid sm:grid-cols-3 sm:gap-6">
            <Toggle label="Necessary" note="Needed for the site to work." checked locked />
            <Toggle
              label="Analytics"
              note="How visitors use the site."
              checked={prefs.analytics}
              onChange={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
            />
            <Toggle
              label="Marketing"
              note="Relevant ads and content."
              checked={prefs.marketing}
              onChange={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
            />
          </div>
        )}
        <div className="flex flex-col md:flex-row md:items-center gap-2.5 md:gap-6">
          <p className="text-[0.9rem] leading-snug text-ink-soft md:flex-1">
            We use cookies to understand site traffic. You choose which to allow.{' '}
            <a href="/privacy-policy" className="underline text-ink hover:text-brand">
              Privacy Policy
            </a>
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 shrink-0">
            <button type="button" onClick={acceptAll} className="rb-btn rb-btn-sm !py-2">
              Accept All
            </button>
            <button
              type="button"
              onClick={necessaryOnly}
              className="min-h-[44px] md:min-h-0 px-4 py-2 rounded-full border border-ink/20 text-[0.9rem] font-semibold text-ink hover:bg-paper transition-colors"
            >
              Necessary Only
            </button>
            {manage ? (
              <button type="button" onClick={() => save(prefs)} className="rb-tap text-[0.9rem] font-semibold text-brand underline">
                Save preferences
              </button>
            ) : (
              <button type="button" onClick={() => setManage(true)} className="rb-tap text-[0.9rem] font-semibold text-ink-soft underline hover:text-ink">
                Manage preferences
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
